/**
 * W-31 §8.2 / §9.1 — the import templates as data. One entry per column of the
 * file the owner fills in, in the order the template writes them.
 *
 *  * `key` is the canonical key `import_rows.raw` must carry. The list is fixed
 *    by migration 20260923000005, whose header comment enumerates both kinds;
 *    a key that is not in that list is simply ignored by the validators.
 *  * `header` is the Turkish text the template prints and the wizard matches a
 *    hand-made file against. A trailing " *" marks a required column, and
 *    `required` is derived from it so the star and the gate can never drift.
 *  * `aliases` are the other spellings a file made by hand uses, already folded
 *    with `foldHeader` (see mapping.ts).
 *  * `width` is the column width in Excel characters (§8.2). The per-column help
 *    texts of the "Açıklama" sheet sit with the workbook writer
 *    (`imports/xlsx.ts`) — the only thing that prints them, and the one module
 *    of this family kept out of the initial bundle.
 *
 * Nothing here is a business number: these are the column names of a
 * spreadsheet, not a figure the owner has to confirm.
 */
import type { ImportKind } from '../constants';

export type ImportFieldType = 'text' | 'number' | 'money' | 'date' | 'bool';

export interface ImportField {
  key: string;
  header: string;
  /** derived from the trailing " *" of `header` — never set by hand */
  required: boolean;
  type: ImportFieldType;
  aliases: string[];
  /** column width in Excel characters (§8.2); absent → derived from the header */
  width?: number;
}

function field(header: string, key: string, type: ImportFieldType, aliases: string[], width?: number): ImportField {
  return { key, header, required: header.endsWith(' *'), type, aliases, ...(width === undefined ? {} : { width }) };
}

/** §8.2 — "Açılış stoğu" v1, the artboard's nine mapped columns plus the four optional lot ones. */
export const OPENING_STOCK_FIELDS: ImportField[] = [
  field('SKU *', 'sku', 'text', ['sku', 'stok kodu', 'urun kodu', 'item code', 'malzeme kodu'], 22),
  field('Ürün adı', 'item_name', 'text', ['urun adi', 'ad', 'name', 'malzeme adi'], 44),
  field('Depo kodu *', 'warehouse_code', 'text', ['depo kodu', 'depo', 'warehouse', 'ambar'], 12),
  field('Lokasyon kodu', 'location_code', 'text', ['lokasyon kodu', 'lokasyon', 'raf', 'location', 'goz'], 16),
  field('Lot no', 'lot_no', 'text', ['lot no', 'lot', 'parti', 'parti no', 'batch'], 16),
  field('Miktar *', 'qty', 'number', ['miktar', 'adet', 'quantity', 'qty', 'sayilan'], 12),
  field('Birim *', 'unit', 'text', ['birim', 'olcu birimi', 'unit', 'uom'], 10),
  field('Birim maliyet', 'unit_cost', 'money', ['birim maliyet', 'maliyet', 'birim fiyat', 'unit cost'], 14),
  field('Para birimi', 'currency', 'text', ['para birimi', 'doviz', 'currency', 'kur birimi'], 12),
  field('Giriş tarihi', 'received_at', 'date', ['giris tarihi', 'uretim tarihi', 'received'], 14),
  field('Son kullanma tarihi', 'expiry', 'date', ['son kullanma tarihi', 'skt', 'expiry', 'son kullanma'], 18),
  field('Tedarikçi', 'supplier', 'text', ['tedarikci', 'supplier', 'satici'], 28),
  field('Not', 'note', 'text', ['not', 'aciklama', 'note', 'remark'], 30),
];

/** §9.1 — "Ürünler" v1. The keys are the ones `private.validate_items` reads. */
export const ITEM_FIELDS: ImportField[] = [
  field('SKU *', 'sku', 'text', ['sku', 'stok kodu', 'urun kodu', 'item code']),
  field('Ad (TR) *', 'name_tr', 'text', ['ad tr', 'ad', 'urun adi', 'name tr']),
  field('Ad (EN)', 'name_en', 'text', ['ad en', 'name en', 'english name']),
  field('Kısa ad', 'short_name', 'text', ['kisa ad', 'short name']),
  field('Tür *', 'type', 'text', ['tur', 'type', 'urun turu']),
  field('Temel birim *', 'base_unit', 'text', ['temel birim', 'base unit', 'ana birim']),
  field('Stok birimi', 'stock_unit', 'text', ['stok birimi', 'stock unit']),
  field('Barkod', 'barcode', 'text', ['barkod', 'barcode', 'gtin', 'ean']),
  field('Ürün grubu', 'item_group', 'text', ['urun grubu', 'grup', 'group']),
  field('Açık ebat (cm)', 'size_cm', 'text', ['acik ebat cm', 'acik ebat', 'ebat', 'size']),
  field('Kat', 'fold', 'text', ['kat', 'fold', 'katlama']),
  field('Gramaj (g/m²)', 'gsm', 'number', ['gramaj g m2', 'gramaj', 'gsm']),
  field('Renk', 'colour', 'text', ['renk', 'colour', 'color']),
  field('Baskı tipi', 'print_type', 'text', ['baski tipi', 'baski', 'print type']),
  field('Paket içi adet', 'pack_qty', 'number', ['paket ici adet', 'paket adedi', 'pack qty']),
  field('Koli içi paket', 'case_qty', 'number', ['koli ici paket', 'koli adedi', 'case qty']),
  field('Palet içi koli', 'pallet_cases', 'number', ['palet ici koli', 'pallet cases']),
  field('Bobin net ağırlığı (kg)', 'roll_kg', 'number', ['bobin net agirligi kg', 'bobin kg', 'roll kg']),
  field('Yeniden sipariş noktası', 'reorder_point', 'number', ['yeniden siparis noktasi', 'reorder point', 'kritik stok']),
  field('Yeniden sipariş miktarı', 'reorder_qty', 'number', ['yeniden siparis miktari', 'reorder qty']),
  field('Tedarik süresi (gün)', 'lead_time_days', 'number', ['tedarik suresi gun', 'tedarik suresi', 'lead time']),
  field('Standart maliyet (₺)', 'standard_cost', 'money', ['standart maliyet', 'standart maliyet try', 'standard cost']),
  field('Lot takibi', 'is_lot_tracked', 'bool', ['lot takibi', 'lot takipli', 'lot tracked']),
  field('Raf ömrü (gün)', 'shelf_life_days', 'number', ['raf omru gun', 'raf omru', 'shelf life']),
  field('GTİP', 'hs_code', 'text', ['gtip', 'gtip no', 'hs code']),
  field('Durum', 'status', 'text', ['durum', 'status']),
];

export interface ImportTemplate {
  kind: ImportKind;
  /** the `$kind` segment of the wizard route */
  slug: ImportSlug;
  version: number;
  /** ASCII file name, without the extension (§8.2) */
  fileBase: string;
  /** the XLSX data sheet this template writes and the reader looks for first (§8.3) */
  sheetName: string;
  /** other spellings of that sheet name, folded with `foldHeader` */
  sheetAliases: string[];
  fields: ImportField[];
  /** false while the RPCs answer 0A000 `phase_locked` for this kind */
  available: boolean;
}

/** URL segment ↔ `import_kind`: the enum is snake_case, the route is kebab-case. */
export const IMPORT_SLUGS = ['items', 'opening-stock', 'contacts', 'opening-balances'] as const;
export type ImportSlug = (typeof IMPORT_SLUGS)[number];

export const IMPORT_TEMPLATES: Record<ImportSlug, ImportTemplate> = {
  items: {
    kind: 'items',
    slug: 'items',
    version: 1,
    fileBase: 'aslen-ops-urunler-sablonu-v1',
    sheetName: 'Ürünler',
    sheetAliases: ['urunler', 'items'],
    fields: ITEM_FIELDS,
    available: true,
  },
  'opening-stock': {
    kind: 'opening_stock',
    slug: 'opening-stock',
    version: 1,
    fileBase: 'aslen-ops-acilis-stogu-sablonu-v1',
    sheetName: 'Açılış stoğu',
    // the spec writes the sheet name "Açılış stoku"; both spellings are read
    sheetAliases: ['acilis stogu', 'acilis stoku', 'opening stock'],
    fields: OPENING_STOCK_FIELDS,
    available: true,
  },
  // Phase 2 stops here: `stage_import` raises 0A000 for both (migration 0005).
  contacts: {
    kind: 'contacts',
    slug: 'contacts',
    version: 1,
    fileBase: 'aslen-ops-cariler-sablonu-v1',
    sheetName: 'Cariler',
    sheetAliases: ['cariler', 'contacts'],
    fields: [],
    available: false,
  },
  'opening-balances': {
    kind: 'opening_balances',
    slug: 'opening-balances',
    version: 1,
    fileBase: 'aslen-ops-acilis-bakiyeleri-sablonu-v1',
    sheetName: 'Açılış bakiyeleri',
    sheetAliases: ['acilis bakiyeleri', 'opening balances'],
    fields: [],
    available: false,
  },
};

export function isImportSlug(value: unknown): value is ImportSlug {
  return typeof value === 'string' && (IMPORT_SLUGS as readonly string[]).includes(value);
}

export function templateOf(slug: ImportSlug): ImportTemplate {
  return IMPORT_TEMPLATES[slug];
}

export function slugOfKind(kind: ImportKind): ImportSlug {
  return kind === 'opening_stock' ? 'opening-stock' : kind === 'opening_balances' ? 'opening-balances' : kind;
}

/** Column headers of a template, in file order. */
export function templateHeaders(slug: ImportSlug): string[] {
  return templateOf(slug).fields.map((f) => f.header);
}

export function requiredFields(slug: ImportSlug): ImportField[] {
  return templateOf(slug).fields.filter((f) => f.required);
}

/**
 * Column width in Excel characters. §8.2 lists one per "Açılış stoğu" column;
 * the other templates get a width wide enough for their own header, so no
 * heading is ever written as "####".
 */
export function fieldWidth(field: ImportField): number {
  return field.width ?? Math.min(40, Math.max(12, field.header.length + 3));
}
